import type { Locale } from "@/i18n/translations";
import { COUNTRY_PAGES } from "./countryPages";

export type PageSection = {
  heading: string;
  paragraphs?: string[];
  list?: string[];
};

export type PageFaq = { question: string; answer: string };

export type LandingPageBody = {
  metaTitle: string;
  metaDescription: string;
  eyebrow: string;
  heading: string;
  intro: string;
  sections: PageSection[];
  faq: PageFaq[];
  faqHeading: string;
  ctaHeading: string;
  ctaBody: string;
  ctaButton: string;
  relatedHeading: string;
};

export type LandingPage = {
  id: string;
  /** URL path per locale, without the leading slash locale segment. */
  path: Record<Locale, string>;
  /** Adds SoftwareApplication schema. */
  isProduct?: boolean;
  content: Record<Locale, LandingPageBody>;
};

const CTA_EN = {
  ctaHeading: "Request an invitation to Nellove",
  ctaBody:
    "Nellove is free forever. Leave your email to request an early-access invitation; access is not immediate.",
  ctaButton: "Request an invitation",
  relatedHeading: "Keep reading",
  faqHeading: "Common questions",
};

const CTA_FR = {
  ctaHeading: "Demandez une invitation pour Nellove",
  ctaBody:
    "Nellove est gratuit pour toujours. Laissez votre e-mail pour demander une invitation d'accès anticipé; l'accès n'est pas immédiat.",
  ctaButton: "Demander une invitation",
  relatedHeading: "À lire ensuite",
  faqHeading: "Questions fréquentes",
};

export const LANDING_PAGES: LandingPage[] = [
  {
    id: "product",
    isProduct: true,
    path: { en: "wedding-planning-app", fr: "application-mariage" },
    content: {
      en: {
        ...CTA_EN,
        metaTitle: "Free wedding planning app — free forever, guests, budget and the day | Nellove",
        metaDescription:
          "Nellove is a wedding planning app: a living plan, guests and RSVP links, a budget ledger and a day-of timeline in one workspace. Free forever.",
        eyebrow: "The product",
        heading: "A wedding planning app, so you don't have to be the project manager",
        intro:
          "Most couples end up running their wedding out of a spreadsheet, a notes app and three group chats. Nellove replaces all of that with one workspace: a plan that knows what is next, your guest list and RSVPs, the money, and the run of the day.",
        sections: [
          {
            heading: "One living plan instead of a static checklist",
            paragraphs: [
              "The home screen shows the next job to do, what a good version of it looks like, and what to ask before you commit. When you are ready for the wider view, See full plan opens the stages — legal, guests, place, money and day-of — so nothing sits forgotten in a chat thread.",
              "The plan is aware of your date. A wedding fourteen months out and a wedding ten weeks out do not get the same next job.",
            ],
          },
          {
            heading: "Guests and RSVP, without a spreadsheet",
            list: [
              "Build the guest list once and keep it as the single source of truth.",
              "Send unique RSVP links, or share one public RSVP form protected by a passcode.",
              "Public replies land in an approval queue so nothing enters your list unchecked.",
              "Free forever, with no guest-count limit.",
            ],
          },
          {
            heading: "A budget ledger that answers the real question",
            paragraphs: [
              "Every line carries what you estimated, what was quoted, what you have paid and what is still due. That is the difference between a budget you look at once and a budget you can act on the week a deposit is owed.",
            ],
          },
          {
            heading: "The day itself, written down",
            paragraphs: [
              "A timeline of moments from the first hair appointment to the last song. You can share the day-of view scoped to the whole day, the ceremony or the reception, and send a link to a provider who only needs their part of it.",
            ],
          },
          {
            heading: "Start in any country, with real depth in five",
            paragraphs: [
              "You can create a wedding anywhere. France, Belgium, the United Kingdom, the United States and Canada have deeper legal guidance built in. Everywhere else starts from an honest general logistics baseline rather than invented legal steps.",
            ],
          },
          {
            heading: "Ask Nellove: nothing is saved until you confirm",
            paragraphs: [
              "Ask Nellove is a companion inside the workspace, not another tab. Ask it to draft a small job from your wedding data. Every change goes through a confirmation step — it never writes to your wedding on its own.",
            ],
          },
          {
            heading: "Plan workshop: choose a direction and let the plan follow",
            paragraphs: [
              "When a decision has several reasonable answers, the plan workshop lets you compare directions, lock one, and have the following jobs adapt to the choice you made.",
            ],
          },
          {
            heading: "English and French, one interface",
            paragraphs: [
              "The whole workspace runs in English or French, so partners and providers do not have to agree on a language before they can agree on a wedding.",
            ],
          },
        ],
        faq: [
          {
            question: "Is Nellove free?",
            answer:
              "Yes. Nellove is free forever, with no guest-count limit on the free offer. You do not need a card to start.",
          },
          {
            question: "How do I get early access?",
            answer: "Join the waitlist and we will email you when your early access is ready.",
          },
          {
            question: "Can we both use the same wedding?",
            answer:
              "Yes. You can invite your partner into the same wedding so you are both working from the same plan.",
          },
          {
            question: "Does it work outside Canada?",
            answer:
              "Yes. You can create a wedding in any country. France, Belgium, the UK, the US and Canada have deeper legal guidance; other countries start from a general logistics baseline.",
          },
        ],
      },
      fr: {
        ...CTA_FR,
        metaTitle: "Application de mariage gratuite pour toujours — invités, budget, jour J | Nellove",
        metaDescription:
          "Nellove est une application de mariage : plan vivant, invités et liens RSVP, budget détaillé et déroulé du jour J dans un seul espace. Gratuit pour toujours.",
        eyebrow: "Le produit",
        heading: "Une application de mariage, pour ne pas devenir chef de projet",
        intro:
          "La plupart des couples organisent leur mariage entre un tableur, une application de notes et trois conversations de groupe. Nellove remplace tout ça par un seul espace : un plan qui sait ce qui vient ensuite, la liste d'invités et les RSVP, l'argent, et le déroulé du jour.",
        sections: [
          {
            heading: "Un plan vivant plutôt qu'une checklist figée",
            paragraphs: [
              "L'accueil affiche la prochaine tâche, à quoi ressemble une bonne version de cette tâche, et les questions à poser avant de vous engager. Quand vous voulez la vue d'ensemble, « Voir le plan complet » ouvre les étapes — légal, invités, lieu, argent, jour J — pour que rien ne dorme au fond d'une discussion.",
              "Le plan connaît votre date. Un mariage dans quatorze mois et un mariage dans dix semaines n'ont pas la même prochaine tâche.",
            ],
          },
          {
            heading: "Invités et RSVP, sans tableur",
            list: [
              "Construisez la liste une fois et gardez-la comme référence unique.",
              "Envoyez des liens RSVP uniques, ou partagez un formulaire public protégé par un code.",
              "Les réponses publiques arrivent dans une file d'approbation : rien n'entre sans contrôle.",
              "Gratuit pour toujours, sans limite d’invités.",
            ],
          },
          {
            heading: "Un budget qui répond à la vraie question",
            paragraphs: [
              "Chaque ligne porte l'estimé, le montant soumissionné, le payé et le reste dû. C'est la différence entre un budget qu'on regarde une fois et un budget utile la semaine où un acompte tombe.",
            ],
          },
          {
            heading: "Le jour J, écrit noir sur blanc",
            paragraphs: [
              "Un déroulé de moments, du premier rendez-vous coiffure à la dernière chanson. Vous pouvez partager la vue jour J pour la journée entière, la cérémonie ou la réception, et envoyer un lien à un prestataire qui n'a besoin que de sa partie.",
            ],
          },
          {
            heading: "Commencez dans n'importe quel pays, avec de la profondeur dans cinq",
            paragraphs: [
              "Vous pouvez créer un mariage partout. La France, la Belgique, le Royaume-Uni, les États-Unis et le Canada bénéficient d'un accompagnement légal plus détaillé. Ailleurs, on part d'une base logistique générale honnête plutôt que d'étapes légales inventées.",
            ],
          },
          {
            heading: "Demander à Nellove : rien n'est enregistré sans votre confirmation",
            paragraphs: [
              "Demander à Nellove est un compagnon dans l'espace de travail, pas un onglet de plus. Demandez-lui de préparer une petite tâche à partir des données de votre mariage. Chaque modification passe par une confirmation : rien ne s'écrit tout seul.",
            ],
          },
          {
            heading: "Atelier de plan : choisissez une direction, le plan suit",
            paragraphs: [
              "Quand une décision a plusieurs réponses raisonnables, l'atelier vous laisse comparer les directions, en verrouiller une, et les tâches suivantes s'adaptent à ce choix.",
            ],
          },
          {
            heading: "Français et anglais, une seule interface",
            paragraphs: [
              "Tout l'espace fonctionne en français ou en anglais : inutile de s'entendre sur une langue avant de s'entendre sur un mariage.",
            ],
          },
        ],
        faq: [
          {
            question: "Nellove est-il gratuit ?",
            answer:
              "Oui. Nellove est gratuit pour toujours, sans limite d’invités pour l’offre gratuite. Aucune carte n'est demandée pour commencer.",
          },
          {
            question: "Comment obtenir un accès anticipé ?",
            answer: "Rejoignez la liste et nous vous écrirons dès que votre accès anticipé sera prêt.",
          },
          {
            question: "Peut-on être deux sur le même mariage ?",
            answer:
              "Oui. Vous pouvez inviter votre partenaire dans le même mariage pour travailler sur le même plan.",
          },
          {
            question: "Est-ce que ça fonctionne hors du Canada ?",
            answer:
              "Oui. Vous pouvez créer un mariage dans n'importe quel pays. La France, la Belgique, le Royaume-Uni, les États-Unis et le Canada ont un accompagnement légal plus détaillé ; ailleurs, on part d'une base logistique générale.",
          },
        ],
      },
    },
  },
  {
    id: "by-country",
    path: { en: "wedding-planning-by-country", fr: "organiser-un-mariage-par-pays" },
    content: {
      en: {
        ...CTA_EN,
        metaTitle: "Planning a wedding in another country — what actually differs | Nellove",
        metaDescription:
          "Getting married abroad or outside your home province? What changes country to country, what never changes, and how Nellove handles countries it does not have deep legal guidance for.",
        eyebrow: "Any country",
        heading: "Planning a wedding in any country, honestly",
        intro:
          "Wedding tools are usually built for one market and quietly assume you live in it. Nellove lets you create a wedding anywhere — and tells you plainly how much legal depth it has for your country instead of pretending it knows.",
        sections: [
          {
            heading: "What changes from country to country",
            list: [
              "Who is legally allowed to marry you, and whether a civil ceremony is separate from the celebration.",
              "Notice and waiting periods before the ceremony.",
              "Documents required, and whether foreign documents need translation or legalisation.",
              "Residency conditions for people marrying outside their home country.",
              "Where and how the marriage is registered afterwards.",
            ],
            paragraphs: [
              "None of these can be guessed safely, which is why Nellove points you to the official source for your location rather than inventing a step.",
            ],
          },
          {
            heading: "What never changes",
            paragraphs: [
              "The logistics are remarkably consistent everywhere: a guest list, invitations and replies, a place, food, people who need to be paid, and an order of events on the day. That baseline is what Nellove gives you in every country from the first minute.",
            ],
          },
          {
            heading: "Where the guidance goes deeper",
            paragraphs: [
              "France, Belgium, the United Kingdom, the United States and Canada have fuller legal guidance built into the plan. If you are marrying in one of those, the legal stage is specific rather than generic.",
            ],
          },
          {
            heading: "Everywhere else: a general baseline, clearly labelled",
            paragraphs: [
              "Create a wedding in any other country and the plan starts from general logistics, with the legal stage telling you to confirm requirements with the authority that will register your marriage. We would rather say so than fill the gap with something that sounds authoritative and is wrong.",
            ],
          },
          {
            heading: "Marrying away from where you live",
            list: [
              "Send save-the-dates earlier than you would locally — guests are booking flights and time off.",
              "Confirm whether your officiant travels, or whether you need a local one.",
              "Ask your venue which suppliers they will and will not let in.",
              "Assume the legal ceremony and the celebration may be on different days, and plan the paperwork first.",
            ],
          },
        ],
        faq: [
          {
            question: "Can I use Nellove if I am not in Canada, France or the UK?",
            answer:
              "Yes. You can create a wedding in any country. Outside the five countries with deep legal guidance, the plan uses a general logistics baseline and asks you to verify legal steps locally.",
          },
          {
            question: "Does Nellove give legal advice?",
            answer:
              "No. It organises the tasks and points you to the official authority for your location. Requirements change, and only the registering authority is authoritative.",
          },
          {
            question: "Which countries have deeper guidance?",
            answer: "France, Belgium, the United Kingdom, the United States and Canada.",
          },
        ],
      },
      fr: {
        ...CTA_FR,
        metaTitle: "Se marier à l'étranger ou ailleurs — ce qui change vraiment | Nellove",
        metaDescription:
          "Mariage à l'étranger ou hors de votre province ? Ce qui change d'un pays à l'autre, ce qui ne change jamais, et comment Nellove traite les pays sans accompagnement légal détaillé.",
        eyebrow: "Tous les pays",
        heading: "Organiser un mariage dans n'importe quel pays, honnêtement",
        intro:
          "Les outils de mariage sont souvent conçus pour un seul marché et supposent que vous y vivez. Nellove vous laisse créer un mariage partout — et vous dit franchement quelle profondeur légale il a pour votre pays plutôt que de faire semblant.",
        sections: [
          {
            heading: "Ce qui change d'un pays à l'autre",
            list: [
              "Qui a le droit de célébrer, et si la cérémonie civile est séparée de la fête.",
              "Les délais de publication et d'attente avant la cérémonie.",
              "Les documents exigés, et la nécessité éventuelle de traduction ou de légalisation.",
              "Les conditions de résidence pour se marier hors de son pays.",
              "L'endroit et la manière dont le mariage est enregistré ensuite.",
            ],
            paragraphs: [
              "Rien de tout cela ne se devine : Nellove vous renvoie à la source officielle de votre lieu plutôt que d'inventer une étape.",
            ],
          },
          {
            heading: "Ce qui ne change jamais",
            paragraphs: [
              "La logistique est remarquablement stable partout : une liste d'invités, des invitations et des réponses, un lieu, un repas, des personnes à payer et un ordre pour la journée. C'est cette base que Nellove fournit dans tous les pays dès la première minute.",
            ],
          },
          {
            heading: "Là où l'accompagnement va plus loin",
            paragraphs: [
              "La France, la Belgique, le Royaume-Uni, les États-Unis et le Canada bénéficient d'un volet légal plus complet dans le plan. Si vous vous mariez dans l'un de ces pays, l'étape légale est précise plutôt que générique.",
            ],
          },
          {
            heading: "Ailleurs : une base générale, clairement annoncée",
            paragraphs: [
              "Créez un mariage dans un autre pays et le plan part de la logistique générale, l'étape légale vous invitant à confirmer les exigences auprès de l'autorité qui enregistrera votre mariage. Nous préférons le dire que combler le vide avec une réponse fausse mais assurée.",
            ],
          },
          {
            heading: "Se marier loin de chez soi",
            list: [
              "Annoncez la date plus tôt qu'en local : vos invités réservent des vols et des congés.",
              "Vérifiez si votre célébrant se déplace ou s'il en faut un sur place.",
              "Demandez au lieu quels prestataires extérieurs sont acceptés.",
              "Partez du principe que la cérémonie légale et la fête peuvent tomber des jours différents, et traitez les papiers en premier.",
            ],
          },
        ],
        faq: [
          {
            question: "Puis-je utiliser Nellove hors de France ou du Canada ?",
            answer:
              "Oui. Vous pouvez créer un mariage dans n'importe quel pays. Hors des cinq pays au volet légal détaillé, le plan utilise une base logistique générale et vous demande de vérifier les démarches localement.",
          },
          {
            question: "Nellove donne-t-il des conseils juridiques ?",
            answer:
              "Non. Il organise les tâches et vous renvoie à l'autorité officielle de votre lieu. Les règles évoluent, et seule l'autorité qui enregistre fait foi.",
          },
          {
            question: "Quels pays ont un accompagnement plus détaillé ?",
            answer: "La France, la Belgique, le Royaume-Uni, les États-Unis et le Canada.",
          },
        ],
      },
    },
  },
  {
    id: "planners",
    path: { en: "for-wedding-planners", fr: "pour-organisateurs-de-mariage" },
    content: {
      en: {
        ...CTA_EN,
        metaTitle: "Wedding planner & agency software, free forever — one workspace per wedding | Nellove",
        metaDescription:
          "For wedding planners and agencies: run each wedding in one workspace instead of six spreadsheets — guests and RSVP, budget ledger, day-of timeline and shareable provider links.",
        eyebrow: "For planners",
        heading: "One workspace per wedding, instead of six spreadsheets",
        intro:
          "Planners rarely lose time on the creative work. They lose it re-typing the same guest count into a spreadsheet, a supplier email and a client update. Nellove keeps one wedding object that you and your clients are both looking at.",
        sections: [
          {
            heading: "The same wedding the couple sees",
            paragraphs: [
              "There is no separate planner copy that drifts out of date. The guest list, the budget lines and the day-of timeline are one record, so a change is a change everywhere.",
            ],
          },
          {
            heading: "What you get per wedding",
            list: [
              "Guest list with unique RSVP links or a public passcode form, plus an approval queue.",
              "Budget ledger: estimated, quoted, paid and remaining due, line by line.",
              "A day-of timeline you can print or share.",
              "Provider links scoped to the whole day, the ceremony or the reception only.",
              "English and French interface for bilingual clients.",
            ],
          },
          {
            heading: "A portfolio view across your weddings",
            paragraphs: [
              "Run every wedding from one account: one workspace per wedding, and a portfolio view to see them all at once.",
            ],
          },
          {
            heading: "Sharing without giving away the whole file",
            paragraphs: [
              "A florist does not need the budget. Share the day-of view scoped to the part of the day that concerns them, with a link, and keep the rest private.",
            ],
          },
        ],
        faq: [
          {
            question: "Can I manage several weddings?",
            answer:
              "Yes — one workspace per wedding, plus a portfolio view across all of them.",
          },
          {
            question: "Is there a cost for planners?",
            answer:
              "Nellove is free forever, with no guest-count limit on the free offer.",
          },
          {
            question: "Can my client see the same plan?",
            answer: "Yes — the couple and the planner work in the same wedding, not on two copies.",
          },
        ],
      },
      fr: {
        ...CTA_FR,
        metaTitle: "Logiciel organisateurs et agences de mariage, gratuit pour toujours | Nellove",
        metaDescription:
          "Pour les wedding planners et agences : gérez chaque mariage dans un seul espace plutôt que six tableurs — invités et RSVP, budget, déroulé du jour J et liens à partager aux prestataires.",
        eyebrow: "Pour les professionnels",
        heading: "Un espace par mariage, plutôt que six tableurs",
        intro:
          "Les organisateurs perdent rarement du temps sur la création. Ils en perdent à retaper le même nombre d'invités dans un tableur, un courriel fournisseur et un point client. Nellove garde un seul mariage, que vous et vos clients regardez ensemble.",
        sections: [
          {
            heading: "Le même mariage que voit le couple",
            paragraphs: [
              "Pas de copie « organisateur » qui se désynchronise. La liste d'invités, les lignes de budget et le déroulé du jour sont un seul enregistrement : une modification vaut partout.",
            ],
          },
          {
            heading: "Ce que vous avez par mariage",
            list: [
              "Liste d'invités avec liens RSVP uniques ou formulaire public à code, et file d'approbation.",
              "Budget : estimé, soumissionné, payé, reste dû, ligne par ligne.",
              "Un déroulé du jour J à imprimer ou à partager.",
              "Des liens prestataires limités à la journée entière, à la cérémonie ou à la réception.",
              "Interface française et anglaise pour vos clients bilingues.",
            ],
          },
          {
            heading: "Une vue portfolio sur tous vos mariages",
            paragraphs: [
              "Gérez tous vos mariages depuis un seul compte : un espace par mariage, et une vue portfolio pour les voir ensemble.",
            ],
          },
          {
            heading: "Partager sans tout ouvrir",
            paragraphs: [
              "Un fleuriste n'a pas besoin du budget. Partagez la vue jour J limitée à la partie qui le concerne, par lien, et gardez le reste privé.",
            ],
          },
        ],
        faq: [
          {
            question: "Puis-je gérer plusieurs mariages ?",
            answer:
              "Oui — un espace par mariage, plus une vue portfolio sur l'ensemble.",
          },
          {
            question: "Y a-t-il un coût pour les professionnels ?",
            answer:
              "Nellove est gratuit pour toujours, sans limite d’invités pour l’offre gratuite.",
          },
          {
            question: "Mon client voit-il le même plan ?",
            answer: "Oui — le couple et l'organisateur travaillent dans le même mariage, pas sur deux copies.",
          },
        ],
      },
    },
  },
  {
    id: "alternative",
    path: { en: "wedding-planning-app-alternative", fr: "alternative-application-mariage" },
    content: {
      en: {
        ...CTA_EN,
        metaTitle: "Wedding planning app alternative — free forever | Nellove",
        metaDescription:
          "Comparing wedding planning tools? What to check before you commit: where your guest list lives, whether the budget tracks what is actually paid, and whether the tool works in your country and language.",
        eyebrow: "Choosing a tool",
        heading: "Choosing a wedding planning app: what to check",
        intro:
          "Most wedding tools look alike on a feature list. The differences that matter show up in month four, when the guest list has changed twice and a deposit is due. Here is what we would check, including where Nellove is honest about its limits.",
        sections: [
          {
            heading: "Does the guest list stay one list?",
            paragraphs: [
              "If invitations, RSVPs and the caterer's head count live in different places, they will disagree. Ask whether replies land back on the same list automatically, and what happens when a guest replies twice.",
              "In Nellove, RSVP replies come back to the same guest list — unique links per guest, or a public passcode form with an approval queue for anything you did not send yourself.",
            ],
          },
          {
            heading: "Does the budget know what has actually been paid?",
            paragraphs: [
              "A budget that only holds estimates is a wish list. Look for estimated, quoted, paid and remaining due on the same line, because the question you will actually ask is what is owed and when.",
            ],
          },
          {
            heading: "Does it work where you are getting married?",
            paragraphs: [
              "Many tools quietly assume one country. Nellove lets you create a wedding anywhere, with deeper legal guidance for France, Belgium, the UK, the US and Canada, and a clearly labelled general baseline elsewhere.",
            ],
          },
          {
            heading: "What does the free tier actually include?",
            paragraphs: [
              "Free trials end in the middle of your planning. Nellove is free forever, without a guest-count limit or a trial countdown.",
            ],
          },
          {
            heading: "What Nellove does not do",
            list: [
              "No provider marketplace — you keep your own provider list and its status.",
            ],
            paragraphs: [
              "If one of those is essential to you today, another tool may fit better, and we would rather you know now.",
            ],
          },
        ],
        faq: [
          {
            question: "How is Nellove different from a spreadsheet?",
            answer:
              "The guest list, the budget and the day-of timeline are one connected record, and RSVP replies come back into it without re-typing.",
          },
          {
            question: "Is there a free plan?",
            answer: "Yes, free forever with no guest-count limit and no card required.",
          },
          {
            question: "Does Nellove have a seating chart?",
            answer: "Yes — place guests at tables in the same workspace as your guest list, and message guests by SMS or WhatsApp.",
          },
        ],
      },
      fr: {
        ...CTA_FR,
        metaTitle: "Quelle application de mariage choisir ? Gratuite pour toujours | Nellove",
        metaDescription:
          "Vous comparez des outils d'organisation de mariage ? Ce qu'il faut vérifier avant de choisir : où vit la liste d'invités, si le budget suit ce qui est réellement payé, et si l'outil fonctionne dans votre pays et votre langue.",
        eyebrow: "Bien choisir",
        heading: "Choisir une application de mariage : ce qu'il faut vérifier",
        intro:
          "Sur une liste de fonctionnalités, tous les outils se ressemblent. Les vraies différences apparaissent au quatrième mois, quand la liste d'invités a changé deux fois et qu'un acompte tombe. Voici ce que nous vérifierions — y compris là où Nellove reconnaît ses limites.",
        sections: [
          {
            heading: "La liste d'invités reste-t-elle une seule liste ?",
            paragraphs: [
              "Si les invitations, les RSVP et le nombre transmis au traiteur vivent à trois endroits, ils finiront par se contredire. Demandez si les réponses reviennent automatiquement dans la même liste, et ce qui se passe quand un invité répond deux fois.",
              "Dans Nellove, les réponses reviennent dans la même liste : liens uniques par invité, ou formulaire public à code avec file d'approbation pour tout ce que vous n'avez pas envoyé vous-même.",
            ],
          },
          {
            heading: "Le budget sait-il ce qui est réellement payé ?",
            paragraphs: [
              "Un budget qui ne contient que des estimations est une liste de souhaits. Cherchez l'estimé, le soumissionné, le payé et le reste dû sur la même ligne : la vraie question sera toujours « combien reste-t-il à payer, et quand ».",
            ],
          },
          {
            heading: "Fonctionne-t-il là où vous vous mariez ?",
            paragraphs: [
              "Beaucoup d'outils supposent un seul pays. Nellove permet de créer un mariage partout, avec un volet légal plus détaillé pour la France, la Belgique, le Royaume-Uni, les États-Unis et le Canada, et une base générale clairement annoncée ailleurs.",
            ],
          },
          {
            heading: "Que contient vraiment la version gratuite ?",
            paragraphs: [
              "Les essais gratuits se terminent au milieu de l'organisation. Nellove est gratuit pour toujours, sans limite d’invités ni compte à rebours.",
            ],
          },
          {
            heading: "Ce que Nellove ne fait pas",
            list: [
              "Pas de place de marché de prestataires — vous gardez votre propre liste et son statut.",
            ],
            paragraphs: [
              "Si l'un de ces points est essentiel pour vous aujourd'hui, un autre outil conviendra peut-être mieux — autant le savoir tout de suite.",
            ],
          },
        ],
        faq: [
          {
            question: "En quoi Nellove diffère-t-il d'un tableur ?",
            answer:
              "La liste d'invités, le budget et le déroulé du jour forment un seul ensemble relié, et les réponses RSVP y reviennent sans ressaisie.",
          },
          {
            question: "Y a-t-il une version gratuite ?",
            answer: "Oui, gratuite pour toujours sans limite d’invités ni carte bancaire.",
          },
          {
            question: "Nellove propose-t-il un plan de table ?",
            answer: "Oui — placez vos invités à table dans le même espace que votre liste d’invités, et envoyez-leur des messages par SMS ou WhatsApp.",
          },
        ],
      },
    },
  },
];

LANDING_PAGES.push(...COUNTRY_PAGES);

export const getLandingPageByPath = (locale: Locale, path: string) =>
  LANDING_PAGES.find((p) => p.path[locale] === path);
